import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Receipt, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function PatientDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments');
      setAppointments(response.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="patient-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Welcome, {user.full_name}
          </h1>
          <p className="text-slate-600 mt-2">View your appointments and medical information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <Calendar className="h-5 w-5 text-[#4A238F]" />
                Your Appointments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <p className="text-slate-500 text-sm">No appointments scheduled</p>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="p-3 bg-slate-50 rounded-md border border-slate-200">
                      <p className="font-medium text-slate-900">{apt.reason}</p>
                      <p className="text-sm text-slate-600 mt-1">{apt.appointment_date} at {apt.appointment_time}</p>
                      <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                        apt.status === 'scheduled' ? 'bg-purple-200 text-blue-700' :
                        apt.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {apt.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <FileText className="h-5 w-5 text-[#4A238F]" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <button className="w-full p-3 text-left bg-slate-50 rounded-md border border-slate-200 hover:bg-slate-100 transition-colors">
                <p className="font-medium text-slate-900">View Medical Records</p>
                <p className="text-sm text-slate-600">Access your health history</p>
              </button>
              <button className="w-full p-3 text-left bg-slate-50 rounded-md border border-slate-200 hover:bg-slate-100 transition-colors">
                <p className="font-medium text-slate-900">View Billing</p>
                <p className="text-sm text-slate-600">Check your invoices</p>
              </button>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}